import { useTranslation } from 'react-i18next';

import { Button } from '@fly/core';
import type { FilterOptionLabel } from '@fly/shared';
import { RadioGroup, Separator } from '@radix-ui/themes';

export type FilterRadioGroupProps = {
  name: string;
  options: { value: string; label: FilterOptionLabel }[];
  value: string | undefined;
  onChange: (value: string) => void;
  onRemove: () => void;
  loading?: boolean;
};

export function FilterRadioGroup({
  name,
  options,
  value,
  onChange,
  onRemove,
  loading = false,
}: FilterRadioGroupProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className='py-fly-3 text-size-fly-2 text-(--fly-color-scheme-gray-11)'>
        {t('app.common.loading', { defaultValue: 'Loading...' })}
      </div>
    );
  }

  return (
    <div>
      <RadioGroup.Root name={name} value={value ?? ''} onValueChange={onChange}>
        {options.map((opt, index, array) => (
          <RadioGroup.Item
            key={opt.value}
            value={opt.value}
            data-testid={`filter-radio-${name}-${opt.value}`}
            className={`w-full py-fly-3 text-size-fly-2 text-custom-12 ${
              index !== array.length - 1
                ? 'border-b-[0.5px] border-(--fly-color-scheme-gray-3)'
                : ''
            }`}
          >
            {t(...opt.label)}
          </RadioGroup.Item>
        ))}
      </RadioGroup.Root>
      {value && (
        <>
          <Separator size='4' className='my-fly-3' />
          <Button
            color='neutral'
            variant='ghost'
            className='w-full'
            size='2'
            onClick={onRemove}
          >
            {t('app.dashboard.table_actions.clear_selection', {
              defaultValue: 'Clear Selection',
            })}
          </Button>
        </>
      )}
    </div>
  );
}
